import { Build } from '@todesktop/shared';
import { Text } from 'ink';
import { useEffect, useState, type JSX } from 'react';
import BuildError from '../../../components/BuildError';
import ErrorDisplay from '../../../components/ErrorDisplay';
import LoadingText from '../../../components/LoadingText';
import CliError from '../../../utilities/CliError';
import getBuildAttributes from '../../../utilities/getBuildAttributes';
import getLatestReleasedBuild from '../../../utilities/getLatestReleasedBuild';
import logger from '../../../utilities/logger';
import {
  isBuildTestable,
  makeProgress,
} from '../../smoke-test/utilities/build';
import analyzeRelease, { AnalyzeResult } from '../utilities/analyzeRelease';
import DowngradeConfirmation from './DowngradeConfirmation';
import YesNoConfirmation from './YesNoConfirmation';

export default function ReleaseConfirmation({
  buildId,
  children,
  configPath,
  disabled = false,
  loadData = loadBuildsData,
}: {
  buildId: string;
  configPath?: string;
  children: JSX.Element;
  disabled?: boolean;

  /** @internal For test */
  loadData?: typeof loadBuildsData;
}) {
  const [state, setState] = useState<ConfirmationState>({
    state: disabled ? 'bypass' : 'loading',
  });

  useEffect(() => {
    confirmationWorkflow({
      buildId,
      configPath,
      loadData,
      state,
      updateState,
    }).catch(logger.error);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function updateState(changes: Partial<ConfirmationState>) {
    setState(
      (previousState) =>
        ({
          ...previousState,
          ...changes,
        }) as ConfirmationState,
    );
  }

  switch (state.state) {
    case 'build-error': {
      return (
        <BuildError
          build={state.build}
          description="Can't release build"
          message={state.error?.message || 'Internal build error'}
        />
      );
    }

    case 'bypass':
      return children;

    case 'confirm-downgrade':
      return (
        <DowngradeConfirmation
          build={state.build}
          latestReleasedBuild={state.latestReleasedBuild}
          confirmDowngrade={state.onConfirm}
        />
      );

    case 'confirm-release':
      if (!state.build) {
        return <ErrorDisplay error={new Error('state.build is empty')} />;
      }

      return (
        <YesNoConfirmation
          title={
            <>
              <Text>This will release build {state.build.id} as </Text>
              <Text bold>
                {state.build.appName} v{state.build.appVersion}
              </Text>
              <Text>, are you sure?</Text>
            </>
          }
          onYes={state.onConfirm}
        />
      );

    case 'confirm-test':
      return (
        <YesNoConfirmation
          title={state.confirmationText}
          onYes={state.onConfirm}
        />
      );

    case 'error':
      return <ErrorDisplay error={state.error} />;

    case 'loading':
      return <LoadingText />;

    default:
      return (
        <ErrorDisplay
          error={
            new Error(`Unknown state ${(state as ConfirmationState).state}`)
          }
        />
      );
  }
}

async function confirmationWorkflow({
  buildId,
  configPath,
  loadData,
  updateState,
  state,
}: {
  buildId: string;
  configPath?: string;
  loadData: typeof loadBuildsData;
  updateState: (state: Partial<ConfirmationState>) => void;
  state: ConfirmationState;
}) {
  try {
    const { build, latestReleasedBuild } = await loadData({
      buildId,
      configPath,
    });
    updateState({ build, latestReleasedBuild });

    const report = analyzeRelease({ build, latestReleasedBuild });
    if (!report.isReleasable) {
      updateState({ error: new Error(report.error), state: 'build-error' });
      return;
    }

    if (state.state !== 'bypass') {
      await confirmDowngrade({ report, updateState });
      await confirmSmokeTest({ build, updateState });
      await confirmRelease({ updateState });
    }

    updateState({ state: 'bypass' });
  } catch (e) {
    updateState({ state: 'error', error: CliError.from(e) });
  }
}

async function confirmDowngrade({
  report,
  updateState,
}: {
  report: AnalyzeResult;
  updateState: (state: Partial<ConfirmationState>) => void;
}) {
  if (!report.isOlderThanLatest) {
    return;
  }

  let onConfirm;
  const downgradePromise = new Promise((r) => (onConfirm = r));

  updateState({ onConfirm, state: 'confirm-downgrade' });

  await downgradePromise;
}

async function confirmSmokeTest({
  build,
  updateState,
}: {
  build: Build;
  updateState: (state: Partial<ConfirmationState>) => void;
}) {
  const confirmationText = getTestConfirmation(build);
  if (confirmationText === '') {
    return;
  }

  let onConfirm;
  const downgradePromise = new Promise((r) => (onConfirm = r));

  updateState({ onConfirm, state: 'confirm-test', confirmationText });

  await downgradePromise;
}

async function confirmRelease({
  updateState,
}: {
  updateState: (state: Partial<ConfirmationState>) => void;
}) {
  let onConfirm;
  const downgradePromise = new Promise((r) => (onConfirm = r));

  updateState({ onConfirm, state: 'confirm-release' });

  await downgradePromise;
}

function getTestConfirmation(build: Build): string {
  const continueText = 'Are you sure you want to release?';

  if (!build.todesktopRuntimeVersionSpecified || !isBuildTestable(build)) {
    return '';
  }

  if (!build.smokeTest) {
    return `Smoke test hasn't been run for this build. ${continueText}`;
  }

  const { total } = makeProgress(build);

  switch (total.state) {
    case 'error':
      return `Smoke test failed for this build. ${continueText}`;
    case 'canceled':
      return `Smoke test was canceled for this build. ${continueText}`;
    case 'progress':
      return `Smoke test is currently running for this build. ${continueText}`;
    default:
      return '';
  }
}

async function loadBuildsData({
  buildId,
  configPath,
}: {
  buildId: string;
  configPath?: string;
}): Promise<{ build: Build; latestReleasedBuild?: Build }> {
  const { appId, build, userId } = await getBuildAttributes({
    buildId,
    configPath,
  });

  const latestReleasedBuild = await getLatestReleasedBuild({ appId, userId });

  return { build, latestReleasedBuild };
}

type ConfirmationState =
  | { state: 'loading' }
  | { state: 'bypass' }
  | {
      state: 'confirm-downgrade';
      build: Build;
      latestReleasedBuild: Build;
      onConfirm: () => void;
    }
  | {
      state: 'confirm-test';
      build: Build;
      confirmationText: string;
      onConfirm: () => void;
    }
  | {
      state: 'confirm-release';
      build: Build;
      onConfirm: () => void;
    }
  | { state: 'error'; error: Error }
  | { state: 'build-error'; build: Build; error: Error };
